import { computed, inject, signal, Signal } from '@angular/core';
import { NotificationService } from './service';
import { BannerStore } from '../banner';
import { MemberStore } from '../member';
import { NotificationEntity } from './model';
import { DocumentStore } from '../document';
import { CorrespondenceStore } from '../chat';
import { EnvironmentService } from '../environment';

export class NotificationStore {
  #service = inject(NotificationService);
  #bannerStore = inject(BannerStore);
  #contactNotifications = this.#getContactInfoNotifications();
  #documentNotifications = this.#getDocumentNotifications();
  #chatNotifications = this.#getChatNotifications();

  notifications = computed(() => {
    const banners = this.#bannerStore.notifications() ?? [];
    const contact = this.#contactNotifications();
    const documents = this.#documentNotifications();
    const chats = this.#chatNotifications();
    return [...banners, ...contact, ...documents, ...chats];
  });

  dismiss(notificationId: string) {
    return this.#service.dismiss(notificationId);
  }

  #getContactInfoNotifications(): Signal<NotificationEntity[]> {
    const memberValue = inject(MemberStore).member.value;
    return computed(() => {
      const member = memberValue();
      const notifications: NotificationEntity[] = [];
      if (!member?.primaryEmail) {
        notifications.push({ id: 'MISSING_EMAIL', label: 'cas.missingEmail' });
      }
      if (!member?.primaryPhone) {
        notifications.push({ id: 'MISSING_PHONE', label: 'cas.missingPhone' });
      }
      return notifications;
    });
  }

  #getDocumentNotifications(): Signal<NotificationEntity[]> {
    const documentsValue = inject(DocumentStore).inboundAndOutbound;
    return computed(() => {
      const documents = documentsValue();
      const unreadOutboundDocuments = documents.filter(document => !document.tags.includes('MemberRead'));
      return unreadOutboundDocuments.length
        ? [
            {
              id: 'UNREAD_DOCUMENTS',
              label: 'cas.youHaveUnreadDocumentsValue',
              count: unreadOutboundDocuments.length,
            },
          ]
        : [];
    });
  }

  #getChatNotifications(): Signal<NotificationEntity[]> {
    const environment = inject(EnvironmentService).environment;
    if (environment.contactInfo.contactForm?.type !== 'Chat') return signal([]).asReadonly();
    const service = inject(NotificationService);
    const store = inject(CorrespondenceStore);
    const isLatestMessageFromCaseWorker = computed(() => !!store.start()?.formTables.chat.tableData.at(-1)?.author);
    const allNotification = computed<NotificationEntity[]>(() => {
      if (isLatestMessageFromCaseWorker()) {
        return [
          {
            id: 'NEW_CHAT_MESSAGE',
            label: 'cas.newChatMessage',
          },
        ];
      }
      return [];
    });
    const preferences = inject(MemberStore).preferences;
    return computed(() => {
      if (!preferences.hasValue() && !preferences.error()) return [];
      const notifications = allNotification();
      return service.isNotificationDismissed(preferences.value(), 'NEW_CHAT_MESSAGE') ? [] : notifications;
    });
  }
}
